import { useState, useEffect } from "react";
import { supabase } from "./supabaseClient";
import PaymentReceipt from "./components/PaymentReceipt";
import PaymentThankYou from "./components/PaymentThankYou";
import PaymentFailed from "./components/PaymentFailed";
import TenantPortal from "./components/TenantPortal";

const IS_RECEIPT_PAGE  = window.location.pathname.startsWith("/tenant/receipt");
const IS_THANKYOU_PAGE = window.location.pathname.startsWith("/tenant/thankyou");
const IS_FAILED_PAGE   = window.location.pathname.startsWith("/tenant/failed");

const C = {
  cream: "#F5F1EA",
  dark: "#163C2F",
  mid: "#214D3A",
  green: "#7FB98E",
  greenPale: "#EAF2ED",
  muted: "#9B9690",
  border: "#D4CFC7",
};

// ── AUTH SCREEN ──────────────────────────────────────────────────────────────
function AuthScreen() {
  const [mode, setMode] = useState("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  const inp = {
    background: "#FFFFFF", border: `1px solid ${C.border}`, borderRadius: 8,
    color: "#2B2B2B", padding: "10px 14px", fontSize: 14, fontFamily: "'Manrope', sans-serif",
    width: "100%", outline: "none", boxSizing: "border-box",
  };
  const lbl = {
    fontSize: 12, color: "#2B2B2B", fontFamily: "'Manrope', sans-serif",
    fontWeight: 500, display: "block", marginBottom: 4,
  };

  const handleGoogle = async () => {
    setLoading(true);
    setError(null);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: window.location.origin + "/tenant" },
    });
    if (error) { setError(error.message); setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);
    if (mode === "signup") {
      if (password !== confirm) {
        setError("Passwords do not match.");
        setLoading(false);
        return;
      }
      const { error } = await supabase.auth.signUp({
        email, password,
        options: { emailRedirectTo: window.location.origin + "/tenant" },
      });
      if (error) setError(error.message);
      else setMessage("Check your email to confirm your account, then sign in.");
    } else {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) setError(error.message);
    }
    setLoading(false);
  };

  return (
    <div style={{
      minHeight: "100vh", background: C.cream, display: "flex",
      alignItems: "center", justifyContent: "center",
      fontFamily: "'Manrope', 'Outfit', sans-serif", padding: 24,
    }}>
      <div style={{ width: "100%", maxWidth: 420 }}>
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginBottom: 10 }}>
            <img src="/leasly-logo.png" alt="Leasly" width="32" height="32" style={{ borderRadius: 6 }} />
            <div style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 38, color: C.dark, lineHeight: 1 }}>
              Leasly
            </div>
          </div>
          <div style={{ display: "inline-block", fontSize: 11, color: C.muted, background: C.cream, border: `1px solid ${C.border}`, borderRadius: 20, padding: "4px 14px", letterSpacing: "0.1em", textTransform: "uppercase" }}>
            Tenant Portal
          </div>
        </div>

        <div style={{
          background: "#FFFFFF", border: `1px solid ${C.border}`, borderRadius: 16,
          padding: "40px 36px", boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        }}>
          <h2 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 26, color: C.dark, marginBottom: 6, textAlign: "center" }}>
            {mode === "signin" ? "Welcome back" : "Create account"}
          </h2>
          <p style={{ fontSize: 13, color: C.muted, textAlign: "center", marginBottom: 28, fontWeight: 300, lineHeight: 1.6 }}>
            {mode === "signin"
              ? "Sign in to view your lease and account details."
              : "Use the email address your landlord has on file."}
          </p>

          <button onClick={handleGoogle} disabled={loading} style={{
            width: "100%", background: "#FFFFFF", border: `1.5px solid ${C.border}`,
            borderRadius: 100, padding: "13px 24px", display: "flex", alignItems: "center",
            justifyContent: "center", gap: 10, fontFamily: "inherit", fontWeight: 600,
            fontSize: 13, color: C.dark, cursor: "pointer", marginBottom: 22, transition: "all 0.2s",
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </button>

          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 22 }}>
            <div style={{ flex: 1, height: 1, background: C.border }} />
            <span style={{ fontSize: 11, color: C.muted, letterSpacing: "0.1em" }}>OR</span>
            <div style={{ flex: 1, height: 1, background: C.border }} />
          </div>

          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div>
              <label style={lbl}>Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="you@example.com" style={inp} />
            </div>
            <div>
              <label style={lbl}>Password</label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} required placeholder="••••••••" style={inp} />
            </div>
            {mode === "signup" && (
              <div>
                <label style={lbl}>Confirm Password</label>
                <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} required placeholder="••••••••" style={inp} />
              </div>
            )}

            {error   && <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#dc2626" }}>{error}</div>}
            {message && <div style={{ background: C.greenPale, border: `1px solid ${C.green}44`, borderRadius: 8, padding: "10px 14px", fontSize: 13, color: C.mid }}>{message}</div>}

            <button type="submit" disabled={loading} style={{
              background: C.dark, color: "#F5F1EA", border: "none", borderRadius: 999,
              padding: "12px 24px", fontWeight: 700, fontSize: 13, cursor: "pointer",
              fontFamily: "'Manrope', sans-serif", letterSpacing: "0.08em", marginTop: 4,
              textTransform: "uppercase", opacity: loading ? 0.7 : 1,
            }}>
              {loading ? "Please wait…" : mode === "signin" ? "Sign In" : "Create Account"}
            </button>
          </form>

          <p style={{ textAlign: "center", fontSize: 12, color: C.muted, marginTop: 20 }}>
            {mode === "signin" ? "Don't have an account? " : "Already have an account? "}
            <button
              onClick={() => { setMode(mode === "signin" ? "signup" : "signin"); setError(null); setMessage(null); }}
              style={{ background: "none", border: "none", color: C.mid, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", fontSize: 12 }}
            >
              {mode === "signin" ? "Sign up" : "Sign in"}
            </button>
          </p>
        </div>

        <p style={{ textAlign: "center", fontSize: 11, color: C.muted, marginTop: 20, lineHeight: 1.6 }}>
          Access is limited to registered tenants.<br />Contact your landlord if you have trouble signing in.
        </p>
      </div>
    </div>
  );
}

// ── ROOT ─────────────────────────────────────────────────────────────────────
export default function TenantApp() {
  const [session, setSession] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setAuthLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (!session) setAuthLoading(false);
    });
    return () => subscription.unsubscribe();
  }, []);

  if (authLoading) return (
    <div style={{ minHeight: "100vh", background: C.cream, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16, fontFamily: "'Manrope', sans-serif" }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={{ width: 28, height: 28, border: `3px solid ${C.border}`, borderTop: `3px solid ${C.dark}`, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
      <div style={{ fontSize: 14, color: C.muted }}>Loading…</div>
    </div>
  );

  if (IS_THANKYOU_PAGE) return <PaymentThankYou />;
  if (IS_FAILED_PAGE) return <PaymentFailed supabase={supabase} />;
  if (IS_RECEIPT_PAGE) return <PaymentReceipt supabase={supabase} />;

  if (!session) return <AuthScreen />;

  return <TenantPortal supabase={supabase} session={session} />;
}
